import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, ChevronRight } from 'lucide-react';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';
import { NavData, SecondaryNavData } from '../Constant';
import SessionsListSection from './SessionsListSection';
import feedaLogo from '../../assets/feedaia-logo.svg';
import logoIcon from '../../assets/logo-icon.svg';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { useAgents } from '../../hooks';
import { cn } from '../../lib/utils';
import { DEFAULT_COPILOT_ID, DEFAULT_COPILOT_NAME } from '../../constants/defaultCopilot';

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useAgents();

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([
        { agent_name: 'All', id: null },
        { agent_name: DEFAULT_COPILOT_NAME, id: DEFAULT_COPILOT_ID },
        ...(getAllAgentData?.data?.agents || []),
      ]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
    }
  }, [collapsed]);

  const isActive = item => {
    const path = `/${item.path}`;
    if (item.exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';

  const linkClass = (active, justifyCenter) =>
    cn(
      'group flex items-center gap-3 px-3 py-2 rounded-[10px] text-sm font-medium transition-all duration-150 relative',
      justifyCenter && 'justify-center',
      active
        ? 'bg-paper-2 text-ink font-semibold'
        : 'text-ink/70 hover:text-ink hover:bg-paper-2/70',
    );

  return (
    <aside
      className={cn(
        'flex-shrink-0 bg-white border-r border-shell-border flex flex-col h-screen sticky top-0 transition-all duration-200 z-50',
        collapsed ? 'w-16' : 'w-64',
      )}>
      <div
        className={cn(
          'h-16 flex items-center border-b border-shell-border flex-shrink-0',
          collapsed ? 'px-3 justify-center gap-1' : 'px-5 justify-between',
        )}>
        <Link to="/dashboard" className="inline-flex items-center min-w-0" aria-label="SpaceBring Labs">
          <img
            src={collapsed ? logoIcon : feedaLogo}
            alt="SpaceBring Labs"
            className={collapsed ? 'w-7 h-7 object-contain' : 'h-7 w-auto max-w-[140px] object-contain'}
          />
        </Link>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 rounded-lg text-mute hover:text-ink hover:bg-paper-2 transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-4">
        <ul className="space-y-1 px-3">
          {NavData.map(item => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  {active ? (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-gradient-to-b from-spacemint to-spacepurple" />
                  ) : null}
                  <Icon
                    className={cn(
                      'w-[18px] h-[18px] flex-shrink-0',
                      active ? 'text-spacepurple' : 'text-ink/60 group-hover:text-ink',
                    )}
                  />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {!collapsed && (
          <div className="mt-6 px-2">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-mute">· Chats</span>
              <Menu
                menuButton={
                  <MenuButton className="text-xs text-mute hover:text-ink bg-transparent border-0 cursor-pointer p-0">
                    {selectedAgent?.label || 'All'}
                  </MenuButton>
                }>
                {agentData?.map((data, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => setSelectedAgent({ label: data?.agent_name, value: data?.id })}>
                    {data?.agent_name}
                  </MenuItem>
                ))}
              </Menu>
            </div>
            <div className="max-h-[220px] overflow-y-auto overflow-x-hidden px-1">
              <SessionsListSection selectedAgent={selectedAgent} />
            </div>
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 border-t border-shell-border px-2 py-3">
        <ul className="space-y-0.5">
          {SecondaryNavData.map(item => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && item.name}
                </NavLink>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={onLogout}
              title={collapsed ? 'Logout' : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-[10px] text-sm font-medium text-ink/70 hover:text-ink hover:bg-paper-2/70 transition-colors',
                collapsed && 'justify-center',
              )}>
              <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
              {!collapsed && 'Logout'}
            </button>
          </li>
        </ul>
      </div>

      <div
        className="border-t border-shell-border p-3 flex-shrink-0"
        onClick={() => navigate('/settings')}
        onKeyDown={e => e.key === 'Enter' && navigate('/settings')}
        role="button"
        tabIndex={0}>
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-2 rounded-[10px] hover:bg-paper-2/70 cursor-pointer transition-colors',
            collapsed && 'justify-center',
          )}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-spacemint to-spacepurple flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {initial}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{displayName}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-mute" />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SideBar;
